/**
 * AOME1 container: client-side encryption of out-of-band media attachments.
 *
 * The wire format is specified in docs/MEDIA_ENCRYPTION.md and MUST stay
 * byte-identical to ios/Tinodios/MediaCrypto.swift — a file encrypted on web is
 * decrypted on iOS and vice versa.
 *
 * Scope note (see the threat model in the spec): this protects media AT REST.
 * The master key is provisioned by the server, so the server is inside the
 * trust boundary for media: a compromised server can unwrap these containers.
 * Keep that scope in mind when reasoning about what this layer defends.
 *
 * Runs on WebCrypto (`crypto.subtle`), which requires a secure context
 * (HTTPS or localhost) in the browser.
 */

// ---------------------------------------------------------------------------
// Constants (must match MediaCrypto.swift)
// ---------------------------------------------------------------------------

export const MAGIC = [0x41, 0x4f, 0x4d, 0x45]; // "AOME"
export const VERSION = 1;
// magic(4) + version(1) + frameSize(4) + plainSize(8) + noncePrefix(8)
export const HEADER_SIZE = 25;
export const TAG_SIZE = 16;
export const KEY_SIZE = 32;
export const NONCE_PREFIX_SIZE = 8;
export const WRAP_NONCE_SIZE = 12;

export const DEFAULT_FRAME_SIZE = 1 << 20; // 1 MiB
export const MAX_FRAME_SIZE = 64 << 20;
export const MAX_FRAME_COUNT = 0xffffffff;

// Domain separator for key wrapping: a wrapped key can never be replayed as a
// file frame, or vice versa.
const KEY_WRAP_AAD = new Uint8Array([0x41, 0x4f, 0x4d, 0x45, 0x31, 0x2f, 0x6b, 0x77]); // "AOME1/kw"

// v2 key-wrap AAD prefix: the full AAD is this prefix followed by the UTF-8
// topic name, which binds a wrapped key to one topic (spec v2 §3.3). Domain
// separated from v1's "AOME1/kw" and from file frames.
const KEY_WRAP_AAD_V2_PREFIX_STR = 'AOME2/kw|';
export const KEY_WRAP_AAD_V2_PREFIX = utf8Bytes(KEY_WRAP_AAD_V2_PREFIX_STR);

/** Metadata format version for per-conversation key wrapping (spec v2 §3.2). */
export const ENC_VERSION_V2 = 2;

/** Thrown for every format/authentication failure. */
export class MediaCryptoError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'MediaCryptoError';
    this.code = code || 'media_crypto_error';
  }
}

function subtle() {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : null;
  if (!c || !c.subtle) {
    throw new MediaCryptoError(
      'WebCrypto is unavailable; a secure context (HTTPS or localhost) is required', 'no_webcrypto');
  }
  return c.subtle;
}

/** True when WebCrypto is usable. Callers fall back to plaintext when false. */
export function isCryptoAvailable() {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : null;
  return !!(c && c.subtle && c.getRandomValues);
}

export function randomBytes(count) {
  const out = new Uint8Array(count);
  globalThis.crypto.getRandomValues(out);
  return out;
}

// ---------------------------------------------------------------------------
// base64 (explicit implementation: identical in Node and the browser, no
// dependency on btoa/Buffer being present or agreeing on edge cases)
// ---------------------------------------------------------------------------

const B64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

export function toBase64(bytes) {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = i + 1 < bytes.length ? bytes[i + 1] : 0;
    const b2 = i + 2 < bytes.length ? bytes[i + 2] : 0;
    out += B64_ALPHABET[b0 >> 2];
    out += B64_ALPHABET[((b0 & 0x03) << 4) | (b1 >> 4)];
    out += i + 1 < bytes.length ? B64_ALPHABET[((b1 & 0x0f) << 2) | (b2 >> 6)] : '=';
    out += i + 2 < bytes.length ? B64_ALPHABET[b2 & 0x3f] : '=';
  }
  return out;
}

export function fromBase64(str) {
  const clean = String(str).replace(/[^A-Za-z0-9+/]/g, '');
  const len = Math.floor((clean.length * 3) / 4);
  const out = new Uint8Array(len);
  let acc = 0;
  let bits = 0;
  let pos = 0;
  for (let i = 0; i < clean.length; i++) {
    const v = B64_ALPHABET.indexOf(clean[i]);
    if (v < 0) {
      continue;
    }
    acc = (acc << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[pos++] = (acc >> bits) & 0xff;
    }
  }
  return pos === len ? out : out.subarray(0, pos);
}

/**
 * UTF-8 encode a string. TextEncoder is present in every browser which has
 * WebCrypto and in Node >= 11, so no polyfill branch is needed.
 */
export function utf8Bytes(str) {
  return new TextEncoder().encode(String(str));
}

function toHex(bytes) {
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export function frameCount(plainSize, frameSize) {
  if (plainSize === 0) {
    return 1;
  }
  return Math.ceil(plainSize / frameSize);
}

/** Exact ciphertext length for a given plaintext length. */
export function encryptedSize(plainSize, frameSize) {
  const fs = frameSize || DEFAULT_FRAME_SIZE;
  return HEADER_SIZE + plainSize + frameCount(plainSize, fs) * TAG_SIZE;
}

function writeBEUint32(view, offset, value) {
  view.setUint32(offset, value >>> 0, false);
}

function writeBEUint64(view, offset, value) {
  // Sizes are bounded by 2 GB here, so the high word is 0 in practice; write it
  // properly anyway so the format is not silently limited to 32 bits.
  const hi = Math.floor(value / 0x100000000);
  const lo = value >>> 0;
  view.setUint32(offset, hi >>> 0, false);
  view.setUint32(offset + 4, lo, false);
}

function readBEUint64(view, offset) {
  return view.getUint32(offset, false) * 0x100000000 + view.getUint32(offset + 4, false);
}

export function buildHeader(frameSize, plainSize, noncePrefix) {
  const header = new Uint8Array(HEADER_SIZE);
  const view = new DataView(header.buffer);
  header.set(MAGIC, 0);
  header[4] = VERSION;
  writeBEUint32(view, 5, frameSize);
  writeBEUint64(view, 9, plainSize);
  header.set(noncePrefix, 17);
  return header;
}

export function parseHeader(bytes) {
  if (!bytes || bytes.length < HEADER_SIZE) {
    throw new MediaCryptoError('Malformed AOME1 header', 'malformed_header');
  }
  const raw = bytes.slice(0, HEADER_SIZE);
  for (let i = 0; i < 4; i++) {
    if (raw[i] !== MAGIC[i]) {
      throw new MediaCryptoError('Not an AOME1 container', 'bad_magic');
    }
  }
  if (raw[4] !== VERSION) {
    throw new MediaCryptoError(`Unsupported container version ${raw[4]}`, 'bad_version');
  }
  const view = new DataView(raw.buffer, raw.byteOffset, raw.byteLength);
  const frameSize = view.getUint32(5, false);
  const plainSize = readBEUint64(view, 9);
  if (frameSize === 0 || frameSize > MAX_FRAME_SIZE) {
    throw new MediaCryptoError(`Invalid frame size ${frameSize}`, 'bad_frame_size');
  }
  if (frameCount(plainSize, frameSize) > MAX_FRAME_COUNT) {
    throw new MediaCryptoError('File exceeds the maximum frame count', 'too_many_frames');
  }
  return {
    frameSize: frameSize,
    plainSize: plainSize,
    noncePrefix: raw.slice(17, 25),
    raw: raw,
    frameCount: frameCount(plainSize, frameSize)
  };
}

/**
 * Cheap sniff used to stay compatible with legacy plaintext media: if the blob
 * does not start with the magic + version, it is not encrypted.
 */
export function looksEncrypted(prefix) {
  if (!prefix || prefix.length < 5) {
    return false;
  }
  for (let i = 0; i < 4; i++) {
    if (prefix[i] !== MAGIC[i]) {
      return false;
    }
  }
  return prefix[4] === VERSION;
}

// ---------------------------------------------------------------------------
// Nonce / AAD derivation
// ---------------------------------------------------------------------------

/** nonce[i] = noncePrefix(8) || uint32_be(i) */
export function frameNonce(noncePrefix, index) {
  const nonce = new Uint8Array(12);
  nonce.set(noncePrefix, 0);
  new DataView(nonce.buffer).setUint32(8, index >>> 0, false);
  return nonce;
}

/** aad[i] = header(25) || uint32_be(i) || finalFlag(1) */
export function frameAAD(header, index, isFinal) {
  const aad = new Uint8Array(HEADER_SIZE + 5);
  aad.set(header, 0);
  new DataView(aad.buffer).setUint32(HEADER_SIZE, index >>> 0, false);
  aad[HEADER_SIZE + 4] = isFinal ? 0x01 : 0x00;
  return aad;
}

// ---------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------

export function makeContentKey() {
  return randomBytes(KEY_SIZE);
}

async function importAesKey(rawKey, usages) {
  if (!rawKey || rawKey.length !== KEY_SIZE) {
    throw new MediaCryptoError(`Expected a 32-byte key, got ${rawKey ? rawKey.length : 0}`, 'bad_key_length');
  }
  return subtle().importKey('raw', bufferOf(rawKey), {name: 'AES-GCM'}, false, usages);
}

// Some WebCrypto implementations reject a Uint8Array view into a larger buffer;
// hand them an exact ArrayBuffer.
function bufferOf(bytes) {
  return bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength ?
    bytes.buffer :
    bytes.slice().buffer;
}

/** keyId = first 8 bytes of SHA-256(masterKey), lowercase hex. */
export async function keyId(masterKey) {
  const digest = await subtle().digest('SHA-256', bufferOf(masterKey));
  return toHex(new Uint8Array(digest).slice(0, 8));
}

export async function wrapKey(contentKey, masterKey) {
  const key = await importAesKey(masterKey, ['encrypt']);
  const nonce = randomBytes(WRAP_NONCE_SIZE);
  const wrapped = await subtle().encrypt(
    {name: 'AES-GCM', iv: nonce, additionalData: KEY_WRAP_AAD, tagLength: 128},
    key, bufferOf(contentKey));
  return {wrapped: new Uint8Array(wrapped), nonce: nonce, keyId: await keyId(masterKey)};
}

export async function unwrapKey(wrapped, nonce, masterKey) {
  if (wrapped.length !== KEY_SIZE + TAG_SIZE || nonce.length !== WRAP_NONCE_SIZE) {
    throw new MediaCryptoError('Malformed wrapped key', 'malformed_header');
  }
  const key = await importAesKey(masterKey, ['decrypt']);
  try {
    const plain = await subtle().decrypt(
      {name: 'AES-GCM', iv: nonce, additionalData: KEY_WRAP_AAD, tagLength: 128},
      key, bufferOf(wrapped));
    return new Uint8Array(plain);
  } catch (err) {
    throw new MediaCryptoError(
      'Could not unwrap the content key: wrong master key, or the metadata was tampered with',
      'unwrap_failed');
  }
}

// ---------------------------------------------------------------------------
// v2 keys: per-conversation wrapping + escrow (docs/MEDIA_ENCRYPTION_V2.md)
//
// The container format is untouched: only the way CK_file is wrapped changes.
// ---------------------------------------------------------------------------

/** aad = UTF8("AOME2/kw|" || topicName) — binds the wrap to version and topic. */
function keyWrapAADv2(topic) {
  if (typeof topic != 'string' || topic.length == 0) {
    throw new MediaCryptoError('A topic name is required to wrap a v2 key', 'bad_topic');
  }
  return utf8Bytes(KEY_WRAP_AAD_V2_PREFIX_STR + topic);
}

/**
 * Wrap a per-file content key under the conversation key.
 *
 * @param {Uint8Array} contentKey - 32-byte CK_file.
 * @param {Uint8Array} convKey - 32-byte CK_conv.
 * @param {string} topic - Tinode topic name, exactly as it appears on the wire.
 * @returns {Promise<{wrapped: Uint8Array, nonce: Uint8Array}>} 48 + 12 bytes.
 */
export async function wrapKeyV2(contentKey, convKey, topic) {
  const aad = keyWrapAADv2(topic);
  const key = await importAesKey(convKey, ['encrypt']);
  const nonce = randomBytes(WRAP_NONCE_SIZE);
  const wrapped = await subtle().encrypt(
    {name: 'AES-GCM', iv: nonce, additionalData: aad, tagLength: 128},
    key, bufferOf(contentKey));
  return {wrapped: new Uint8Array(wrapped), nonce: nonce};
}

/**
 * Unwrap a v2-wrapped content key. Throws `unwrap_failed` when the tag does not
 * verify — wrong conversation key, wrong topic, or tampered metadata.
 *
 * @returns {Promise<Uint8Array>} the 32-byte CK_file.
 */
export async function unwrapKeyV2(wrapped, nonce, convKey, topic) {
  if (!wrapped || !nonce || wrapped.length !== KEY_SIZE + TAG_SIZE || nonce.length !== WRAP_NONCE_SIZE) {
    throw new MediaCryptoError('Malformed wrapped key', 'malformed_header');
  }
  const aad = keyWrapAADv2(topic);
  const key = await importAesKey(convKey, ['decrypt']);
  try {
    const plain = await subtle().decrypt(
      {name: 'AES-GCM', iv: nonce, additionalData: aad, tagLength: 128},
      key, bufferOf(wrapped));
    return new Uint8Array(plain);
  } catch (err) {
    throw new MediaCryptoError(
      'Could not unwrap the content key: wrong conversation key or topic, or the metadata was tampered with',
      'unwrap_failed');
  }
}

/** ckid = lowercase hex of the first 8 bytes of SHA-256(CK_conv) — 16 chars. */
export async function convKeyId(convKey) {
  if (!convKey || convKey.length !== KEY_SIZE) {
    throw new MediaCryptoError(
      `Expected a 32-byte conversation key, got ${convKey ? convKey.length : 0}`, 'bad_key_length');
  }
  const digest = await subtle().digest('SHA-256', bufferOf(convKey));
  return toHex(new Uint8Array(digest).slice(0, 8));
}

/**
 * Decode a PEM-armoured SubjectPublicKeyInfo into DER bytes.
 * Accepts any PEM label; the armour is stripped, not validated, because
 * `importKey` is what actually rejects a non-SPKI body.
 */
export function pemToDer(pem) {
  if (typeof pem != 'string' || pem.indexOf('-----BEGIN') < 0) {
    throw new MediaCryptoError('Escrow public key is not PEM-encoded', 'bad_escrow_key');
  }
  const body = pem
    .replace(/-----BEGIN [^-]+-----/g, '')
    .replace(/-----END [^-]+-----/g, '')
    .replace(/\s+/g, '');
  const der = fromBase64(body);
  if (!der || der.length == 0) {
    throw new MediaCryptoError('Escrow public key is empty', 'bad_escrow_key');
  }
  return der;
}

/**
 * Wrap a content key to the ops escrow public key (spec v2 §3.4):
 * RSA-OAEP, SHA-256, empty label (the WebCrypto default).
 *
 * @param {Uint8Array} contentKey - 32-byte CK_file. Never CK_conv.
 * @param {string} spkiPem - PEM SPKI public key from the convkey response.
 * @returns {Promise<Uint8Array>} the RSA ciphertext (512 B for RSA-4096).
 */
export async function escrowWrap(contentKey, spkiPem) {
  if (!contentKey || contentKey.length !== KEY_SIZE) {
    throw new MediaCryptoError(
      `Expected a 32-byte content key, got ${contentKey ? contentKey.length : 0}`, 'bad_key_length');
  }
  const der = pemToDer(spkiPem);
  let pub;
  try {
    pub = await subtle().importKey(
      'spki', bufferOf(der), {name: 'RSA-OAEP', hash: 'SHA-256'}, false, ['encrypt']);
  } catch (err) {
    throw new MediaCryptoError(
      `Could not import the escrow public key: ${err.message}`, 'bad_escrow_key');
  }
  const ct = await subtle().encrypt({name: 'RSA-OAEP'}, pub, bufferOf(contentKey));
  return new Uint8Array(ct);
}

// ---------------------------------------------------------------------------
// Frame primitives
// ---------------------------------------------------------------------------

async function sealFrame(key, plain, nonce, aad) {
  const out = await subtle().encrypt(
    {name: 'AES-GCM', iv: nonce, additionalData: aad, tagLength: 128}, key, bufferOf(plain));
  return new Uint8Array(out);
}

async function openFrame(key, frame, nonce, aad) {
  if (frame.length < TAG_SIZE) {
    throw new MediaCryptoError('Ciphertext is truncated', 'truncated');
  }
  try {
    const out = await subtle().decrypt(
      {name: 'AES-GCM', iv: nonce, additionalData: aad, tagLength: 128}, key, bufferOf(frame));
    return new Uint8Array(out);
  } catch (err) {
    throw new MediaCryptoError(
      'Decryption failed: data is corrupt or was tampered with', 'auth_failed');
  }
}

// ---------------------------------------------------------------------------
// In-memory byte API (small payloads, tests, known-answer vectors)
// ---------------------------------------------------------------------------

export async function encryptBytes(plain, contentKey, opts) {
  const options = opts || {};
  const frameSize = options.frameSize || DEFAULT_FRAME_SIZE;
  if (frameSize <= 0 || frameSize > MAX_FRAME_SIZE) {
    throw new MediaCryptoError(`Invalid frame size ${frameSize}`, 'bad_frame_size');
  }
  const noncePrefix = options.noncePrefix || randomBytes(NONCE_PREFIX_SIZE);
  const key = await importAesKey(contentKey, ['encrypt']);
  const header = buildHeader(frameSize, plain.length, noncePrefix);
  const n = frameCount(plain.length, frameSize);
  if (n > MAX_FRAME_COUNT) {
    throw new MediaCryptoError('File exceeds the maximum frame count', 'too_many_frames');
  }

  const out = new Uint8Array(encryptedSize(plain.length, frameSize));
  out.set(header, 0);
  let offset = HEADER_SIZE;
  for (let i = 0; i < n; i++) {
    const chunk = plain.subarray(i * frameSize, Math.min((i + 1) * frameSize, plain.length));
    const sealed = await sealFrame(
      key, chunk, frameNonce(noncePrefix, i), frameAAD(header, i, i === n - 1));
    out.set(sealed, offset);
    offset += sealed.length;
  }
  return out;
}

export async function decryptBytes(blob, contentKey) {
  const header = parseHeader(blob);
  const key = await importAesKey(contentKey, ['decrypt']);
  const out = new Uint8Array(header.plainSize);
  let inOffset = HEADER_SIZE;
  let outOffset = 0;
  for (let i = 0; i < header.frameCount; i++) {
    const isFinal = i === header.frameCount - 1;
    const plainLen = isFinal ? header.plainSize - i * header.frameSize : header.frameSize;
    const frameLen = plainLen + TAG_SIZE;
    if (inOffset + frameLen > blob.length) {
      throw new MediaCryptoError('Ciphertext is truncated', 'truncated');
    }
    const plain = await openFrame(
      key, blob.subarray(inOffset, inOffset + frameLen),
      frameNonce(header.noncePrefix, i), frameAAD(header.raw, i, isFinal));
    out.set(plain, outOffset);
    outOffset += plain.length;
    inOffset += frameLen;
  }
  if (inOffset !== blob.length) {
    throw new MediaCryptoError('Unexpected trailing bytes after final frame', 'trailing_garbage');
  }
  return out;
}

// ---------------------------------------------------------------------------
// Blob API (what the upload/download paths actually use)
// ---------------------------------------------------------------------------

/**
 * Encrypt a Blob/File one frame at a time.
 *
 * Only a single plaintext frame is decoded at any moment. The ciphertext parts
 * are handed to the Blob constructor, which browsers back with disk storage for
 * large payloads — see the streaming caveat in docs/MEDIA_ENCRYPTION.md, this
 * is not equivalent to the fully streaming iOS path.
 */
export async function encryptBlob(blob, contentKey, opts) {
  const options = opts || {};
  const frameSize = options.frameSize || DEFAULT_FRAME_SIZE;
  const noncePrefix = options.noncePrefix || randomBytes(NONCE_PREFIX_SIZE);
  const onProgress = options.onProgress;

  const plainSize = blob.size;
  const key = await importAesKey(contentKey, ['encrypt']);
  const header = buildHeader(frameSize, plainSize, noncePrefix);
  const n = frameCount(plainSize, frameSize);
  if (n > MAX_FRAME_COUNT) {
    throw new MediaCryptoError('File exceeds the maximum frame count', 'too_many_frames');
  }

  const parts = [header];
  let done = 0;
  for (let i = 0; i < n; i++) {
    const start = i * frameSize;
    const end = Math.min(start + frameSize, plainSize);
    const chunk = new Uint8Array(await blob.slice(start, end).arrayBuffer());
    parts.push(await sealFrame(
      key, chunk, frameNonce(noncePrefix, i), frameAAD(header, i, i === n - 1)));
    done += chunk.length;
    if (onProgress) {
      onProgress(done, plainSize);
    }
  }
  return new Blob(parts, {type: 'application/octet-stream'});
}

/**
 * Decrypt a Blob produced by `encryptBlob`. `mimeType` is applied to the result
 * so the caller can hand it straight to `URL.createObjectURL`.
 */
export async function decryptBlob(blob, contentKey, opts) {
  const options = opts || {};
  const onProgress = options.onProgress;

  const header = parseHeader(new Uint8Array(await blob.slice(0, HEADER_SIZE).arrayBuffer()));
  const key = await importAesKey(contentKey, ['decrypt']);

  const parts = [];
  let offset = HEADER_SIZE;
  let done = 0;
  for (let i = 0; i < header.frameCount; i++) {
    const isFinal = i === header.frameCount - 1;
    const plainLen = isFinal ? header.plainSize - i * header.frameSize : header.frameSize;
    const frameLen = plainLen + TAG_SIZE;
    if (offset + frameLen > blob.size) {
      throw new MediaCryptoError('Ciphertext is truncated', 'truncated');
    }
    const frame = new Uint8Array(await blob.slice(offset, offset + frameLen).arrayBuffer());
    parts.push(await openFrame(
      key, frame, frameNonce(header.noncePrefix, i), frameAAD(header.raw, i, isFinal)));
    offset += frameLen;
    done += plainLen;
    if (onProgress) {
      onProgress(done, header.plainSize);
    }
  }
  if (offset !== blob.size) {
    throw new MediaCryptoError('Unexpected trailing bytes after final frame', 'trailing_garbage');
  }
  return new Blob(parts, {type: options.mimeType || 'application/octet-stream'});
}

// ---------------------------------------------------------------------------
// Drafty entity metadata ("enc" marker)
// ---------------------------------------------------------------------------

/** Build the `enc` object that goes into a Drafty entity's data dictionary. */
export function makeEncMetadata(wrappedKey, plainSize, frameSize) {
  return {
    v: VERSION,
    alg: 'A256GCM',
    kw: toBase64(wrappedKey.wrapped),
    kn: toBase64(wrappedKey.nonce),
    kid: wrappedKey.keyId,
    fs: frameSize || DEFAULT_FRAME_SIZE,
    ps: plainSize
  };
}

/**
 * Build the v2 `enc` object (spec v2 §3.2).
 *
 * `esc` is present if and only if `escrowCt` is supplied — the key is entirely
 * absent otherwise, never null and never undefined, because "escrow was off"
 * must be provable by the absence of the field.
 *
 * @param {Object} p
 * @param {Uint8Array} p.wrapped - 48-byte wrapped CK_file.
 * @param {Uint8Array} p.nonce - 12-byte wrap nonce.
 * @param {string} p.ckid - 16 hex chars.
 * @param {string} p.topic - topic the wrap is bound to.
 * @param {number} p.plainSize
 * @param {number} [p.frameSize]
 * @param {Uint8Array|string} [p.escrowCt] - RSA ciphertext (bytes or base64).
 * @param {string} [p.escrowAlg] - from the convkey response.
 * @param {string} [p.escrowKid] - from the convkey response.
 */
export function makeEncMetadataV2(p) {
  const params = p || {};
  const enc = {
    v: ENC_VERSION_V2,
    alg: 'A256GCM',
    kw: toBase64(params.wrapped),
    kn: toBase64(params.nonce),
    ckid: params.ckid,
    tpc: params.topic,
    fs: params.frameSize || DEFAULT_FRAME_SIZE,
    ps: params.plainSize
  };
  if (params.escrowCt) {
    enc.esc = {
      alg: params.escrowAlg || 'RSA-OAEP-256',
      kid: params.escrowKid || 'ops-escrow-rsa:v1',
      ct: typeof params.escrowCt == 'string' ? params.escrowCt : toBase64(params.escrowCt)
    };
  }
  return enc;
}

/**
 * Read the `enc` marker off an entity data dict. Returns null when the
 * attachment is legacy plaintext — that is the normal, expected case for
 * everything sent before this feature shipped — and also for an `enc.v` this
 * build does not understand, which is never guessed at (spec v2 §3.5).
 */
export function parseEncMetadata(data) {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const enc = data.enc;
  if (!enc || typeof enc !== 'object') {
    return null;
  }
  if (!enc.kw || !enc.kn) {
    return null;
  }

  if (enc.v === ENC_VERSION_V2) {
    const esc = (enc.esc && typeof enc.esc == 'object' && enc.esc.ct) ? {
      alg: enc.esc.alg || '',
      kid: enc.esc.kid || '',
      ct: enc.esc.ct
    } : null;
    return {
      version: ENC_VERSION_V2,
      alg: enc.alg || 'A256GCM',
      wrapped: fromBase64(enc.kw),
      nonce: fromBase64(enc.kn),
      ckid: enc.ckid || '',
      topic: typeof enc.tpc == 'string' ? enc.tpc : '',
      // Hints only. The authenticated header is authoritative.
      frameSizeHint: enc.fs || DEFAULT_FRAME_SIZE,
      plainSizeHint: typeof enc.ps === 'number' ? enc.ps : -1,
      escrow: esc
    };
  }

  if (enc.v !== VERSION) {
    // Unknown version: cannot read it, must not guess at it.
    return null;
  }

  return {
    version: enc.v,
    alg: enc.alg || 'A256GCM',
    wrapped: fromBase64(enc.kw),
    nonce: fromBase64(enc.kn),
    keyId: enc.kid || '',
    // Hints only. The authenticated header is authoritative.
    frameSizeHint: enc.fs || DEFAULT_FRAME_SIZE,
    plainSizeHint: typeof enc.ps === 'number' ? enc.ps : -1
  };
}

/** True when this entity data advertises an encrypted payload. */
export function isEncrypted(data) {
  return parseEncMetadata(data) != null;
}

/**
 * Full send-side helper: encrypt a blob under a fresh content key and return
 * both the ciphertext blob and the `enc` metadata to stamp onto the entity.
 */
export async function encryptForUpload(blob, masterKey, opts) {
  const options = opts || {};
  const frameSize = options.frameSize || DEFAULT_FRAME_SIZE;
  const contentKey = makeContentKey();
  const encrypted = await encryptBlob(blob, contentKey, {
    frameSize: frameSize,
    onProgress: options.onProgress
  });
  const wrapped = await wrapKey(contentKey, masterKey);
  return {blob: encrypted, enc: makeEncMetadata(wrapped, blob.size, frameSize)};
}

/**
 * Full receive-side helper: unwrap the content key from entity metadata and
 * decrypt. Returns the blob untouched when the entity carries no `enc` marker
 * or the payload is not actually an AOME1 container (legacy plaintext).
 */
export async function decryptFromDownload(blob, data, masterKey, opts) {
  const options = opts || {};
  const meta = parseEncMetadata(data);
  if (!meta) {
    return blob;
  }
  const head = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
  if (!looksEncrypted(head)) {
    // Marked as encrypted but the bytes are not a container — a partially
    // migrated file. Pass it through rather than failing the render.
    return blob;
  }
  if (meta.version !== VERSION) {
    // v2 metadata is keyed per conversation; the caller must route it through
    // the conversation-key path in media-fetch.js instead.
    throw new MediaCryptoError(
      `enc.v ${meta.version} cannot be decrypted with the app-wide master key`,
      'unsupported_enc_version');
  }
  if (!masterKey) {
    throw new MediaCryptoError('Media encryption key is unavailable', 'no_master_key');
  }
  const contentKey = await unwrapKey(meta.wrapped, meta.nonce, masterKey);
  return decryptBlob(blob, contentKey, {
    mimeType: options.mimeType,
    onProgress: options.onProgress
  });
}
